import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { map, Observable, of, startWith } from 'rxjs';
import { AssessmentService } from 'src/app/services/assessment.service';
import { ReportService } from 'src/app/services/report.service';
import { UserService } from 'src/app/services/user.service';
@Component({
  selector: 'app-reports-filters',
  standalone: false,
  templateUrl: './reports-filters.component.html',
  styleUrl: './reports-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ReportsFiltersComponent {
  //dropdown lists
  regions: any;
  statuses: any;
  complexities: any;
  team_leads: any;
  yearsArray: any;
  states: any;
  reportStatuses: any;
  roles: any;
  skills: any;

  //forest name filter
  forestNameList: string[];
  filtered_forest_names: Observable<string[]>;
  @ViewChild('forest') input?: ElementRef<HTMLInputElement>;
  selectedRegion: string | undefined = undefined;
  selectedForestFlag = false;

  //parent/child data
  @Input() selectedColumns: any;
  @Output() sendRun = new EventEmitter();

  filters = this.formBuilder.group({
    //location
    region: [''],//assessment
    forest_name: [''],//assessment
    forest_org_code: [''],//assessment
    IRWIN_POO_state: [''],//assessment
    ranger_district: [''], //report/forest/ranger_district
    IRWIN_POO_county: [''],//assessment
    //date
    calendar_year: [''],//assessment/fire_discovery_date
    fiscal_year: [''],//report/authorized_on
    //fire details
    baer_name: [''],//assessment
    fire_start_date: [''],//assessment/tentative_start_week
    IRWIN_unique_fire_identifier: [''],//assessment
    job_code: [''],//assessment
    total_fire_acres: [''],//assessment
    source: [''],//assessment
    acres_fsonly: [''],//assessment/est_fire_acres
    fire_complexity: [''],//assessment/fire_complexity_id
    containment_date_est: [''],//assessment
    containment_date: [''],//assessment
    //assessment
    assessment_status: [''],//assessment/assessment_status_id
    team_lead: [''],//assessment/team_lead_id or reports team lead logic
    team_needs: [''],//assessment/team_needs_id
    trainees_needed: [''],//assessment
    tentative_start_week: [''],//assessment
    close_out_date: [''],//assessment
    //report
    report_baer_name: [''],//report
    report_status: [''],//report/report_status_id
    report_id: [''],//report
    report_type: [''],//report
    action_type: [''],//report/report_num calc
    authorization_status: [''],//report/authorization_status_id
    submission_date: [''],//report
    team_members: [''],//report_member/user
    total_funding_req: [''],//treatments calc
    ro_auth: [''],//treatments calc
    wo_auth: [''],//treatments calc
    total_auth: [''],//treatments calc
    //treatment totals
    treatment_all: [''],//treatments calc
    treatment_unit: [''],//treatments calc
    treatment_plants: [''],//treatments calc
    treatment_ground_cover: [''],//treatments calc
    treatment_channel: [''],//treatments calc
    treatment_heritage: [''],//treatments calc
    treatment_roads: [''],//treatments calc
    treatment_safety: [''],//treatments calc
    treatment_monitoring: [''],//treatments calc
    //soil burn severity acres
    soil_burn_sev_developed: [''],//soil_burn_severity calc
    soil_burn_sev_unburned: [''],//soil_burn_severity calc
    soil_burn_sev_low: [''],//soil_burn_severity calc
    soil_burn_sev_moderate: [''],//soil_burn_severity calc
    soil_burn_sev_high: [''],//soil_burn_severity calc
    //user
    name: [''],//user/first and last name
    agency: [''],//user/location_id
    skills_list: [''],//user
    skills_list_read: [''],//user skills list calc
    skill_level: [''],//user skills_list
    fire_assignment: [''],//user calc on report_member
    bear_role: [''],//user/baer_role
  });

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly assessmentService: AssessmentService,
    private readonly reportService: ReportService,
    private readonly userService: UserService,
  ) {
    this.regions = this.assessmentService.getRegionList();
    this.statuses = this.assessmentService.getStatusList();
    this.complexities = this.assessmentService.getComplexityList();
    this.userService.getPossibleLeadList().subscribe((team_leads: any) => { this.team_leads = team_leads.users });
    this.forestNameList = this.assessmentService.getForestNameList();
    this.filtered_forest_names = of(this.forestNameList);
    this.states = this.reportService.getStateList();
    this.reportStatuses = this.reportService.getReportStatuses().subscribe((report_statuses: any) => { this.reportStatuses = report_statuses });
    this.userService.getSkillList().subscribe((skills: any) => { this.skills = skills })
    this.roles = this.userService.getRoleList();

    this.assessmentService.finishLoadingSubscription().subscribe(() => {
      this.forestNameList = this.assessmentService.getForestNameList(this.filters.value.region as any);

      this.filtered_forest_names = of(this.forestNameList)
      this.filtered_forest_names = this.filters.controls.forest_name.valueChanges.pipe(
        startWith(""),
        map((value) => this._filter((value as string) || ""))
      )
    })

    const currentYear = new Date().getFullYear();
    const numberOfYears = 3; // You can change this to the desired number of years
    this.yearsArray = Array.from({ length: numberOfYears }, (_, i) => currentYear - i);
  }

  filterNeeded(name: string) {
    if(this.selectedColumns.find((item: any) => item.column == name)) return true;
    return false;
  }

  clearFilters() {
    this.filters.reset();
  }

  runReport() {
    let selectedFilters = this.selectedColumns.filter((column: any) => this.filters.get(column.column)?.value)
    let formattedFilters = selectedFilters.map((column: any) => { return '&' + column.column + '=' + this.filters.get(column.column)?.value})
    this.sendRun.emit(formattedFilters.join('') ?? '');
  }

  regionChanged(): void {
    this.forestNameList = this.assessmentService.getForestNameList(this.filters.value.region as any);
    this.filtered_forest_names = this.filters.controls.forest_name.valueChanges.pipe(
      startWith(""),
      map((value) => this._filter((value as string) || ""))
    )
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase()
    return this.forestNameList.filter((option: string) =>
      option.toLowerCase().includes(filterValue)
    )
  }
  filter(): void {
    const filterValue = this.input?.nativeElement.value.toLowerCase() ?? '';
    this.filtered_forest_names = of(this._filter(filterValue));
  }
  clearForest() {
    if (!this.selectedForestFlag) {
      this.filters.controls.forest_name.setValue("")
    }
    this.selectedForestFlag = false
  }

  forestSelected() {
    this.selectedForestFlag = true
  }
}
